export type KidExplainItem = {
  id: string;
  title: string;
  action: string;
};

type KidLines = { en: string[]; es: string[] };

const KID_LINES: Record<string, KidLines> = {
  'insurance-epli': {
    en: [
      'This paper pays if a worker says the company was unfair.',
      'It is not the paper that pays if a worker gets hurt.',
      'Do not buy it until someone works here and gets a paycheck.',
      'If nobody works here yet, write that down.',
      'Do not make up a paper.',
    ],
    es: [
      'Este papel paga si un trabajador dice que la empresa fue injusta.',
      'No es el papel que paga si un trabajador se lastima.',
      'No lo compres hasta que alguien trabaje aquí y reciba un sueldo.',
      'Si todavía nadie trabaja aquí, escríbelo.',
      'No inventes un papel.',
    ],
  },
  'insurance-eo-cgl': {
    en: [
      'One paper pays if the company’s advice was wrong.',
      'The other paper pays if the work hurts someone or breaks their things.',
      'The paper has to use the company’s real name.',
      'Do not make up a paper.',
    ],
    es: [
      'Un papel paga si el consejo de la empresa estuvo mal.',
      'El otro papel paga si el trabajo lastima a alguien o rompe sus cosas.',
      'El papel tiene que usar el nombre real de la empresa.',
      'No inventes un papel.',
    ],
  },
  'apex-accelerator': {
    en: [
      'APEX Accelerator is a free government office that shows a small company how to sell to the government.',
      'You book a meeting. They can review the vendor registration, the one-page description of the work, and the plan for the first bid.',
      'This page does not book the meeting.',
      'You are done when the meeting date and notes are saved here. Do not make up a paper.',
    ],
    es: [
      'APEX Accelerator es una oficina gratuita del gobierno que enseña a una empresa pequeña a venderle al gobierno.',
      'Usted reserva una cita. Pueden revisar el registro de proveedor, la descripción de una página del trabajo y el plan de la primera oferta.',
      'Esta página no reserva la cita.',
      'Termina cuando la fecha de la cita y las notas están guardadas aquí. No invente un papel.',
    ],
  },
  'sam-uei-cage': {
    en: [
      'The government keeps a list of companies that want to sell to it.',
      'You ask to be on that list. They give you two numbers when they say yes.',
      'This page does not sign you up and does not invent those numbers.',
      'You are done when the real numbers are saved here.',
    ],
    es: [
      'El gobierno tiene una lista de empresas que quieren venderle.',
      'Pides estar en esa lista. Te dan dos números cuando dicen que sí.',
      'Esta página no te inscribe y no inventa esos números.',
      'Terminas cuando los números de verdad están guardados aquí.',
    ],
  },
  'fci-cui-determination': {
    en: [
      'This step reads the job paper and picks one label: FCI, CUI, or Classified.',
      'FCI is everyday contract information that is not public. It goes with CMMC Level 1 (a self-check). It does not need CMMC Level 2 and it does not need a facility clearance.',
      'CUI is more sensitive unclassified information. It goes with NIST 800-171 and CMMC Level 2. CUI is still not Secret or Top Secret.',
      'Classified means the paper says Secret, Top Secret, or a facility clearance (FCL). An owner’s personal clearance is not the company’s FCL. Do not bid that work until the company holds what the notice asks for.',
      'Use Help me decide to answer three yes-or-no questions from the paper. Do not guess. You are done when the real extract or a true search note is saved here.',
    ],
    es: [
      'Este paso lee el papel del trabajo y elige una etiqueta: FCI, CUI o Clasificado.',
      'FCI es información cotidiana del contrato que no es pública. Va con CMMC Nivel 1 (una autoevaluación). No necesita CMMC Nivel 2 ni una habilitación de instalaciones.',
      'CUI es información no clasificada más sensible. Va con NIST 800-171 y CMMC Nivel 2. CUI todavía no es Secret ni Top Secret.',
      'Clasificado significa que el papel dice Secret, Top Secret o una habilitación de instalaciones (FCL). La habilitación personal de un dueño no es el FCL de la empresa. No oferte ese trabajo hasta que la empresa tenga lo que pide el aviso.',
      'Use Ayúdame a decidir para responder tres preguntas de sí o no según el papel. No adivine. Termina cuando el extracto real o una nota verdadera de la búsqueda esté guardada aquí.',
    ],
  },
};

const splitSentences = (text: string): string[] => (
  text
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => sentence.trim())
    .filter(Boolean)
);

export const kidExplain = (item: KidExplainItem, language: 'en' | 'es'): string[] => {
  const custom = KID_LINES[item.id];
  if (custom) {
    return custom[language];
  }
  const lead = language === 'es'
    ? `Este paso se llama “${item.title}”.`
    : `This step is called “${item.title}”.`;
  const close = language === 'es'
    ? 'Terminas cuando el papel de verdad está guardado aquí. No inventes uno.'
    : 'You are done when the real paper is saved here. Do not make one up.';
  return [lead, ...splitSentences(item.action).slice(0, 3), close];
};

export const noteCanStandInForProof = (evidenceHint: string): boolean => (
  /note|n\/a|wait/i.test(evidenceHint)
);
